// The one appearance picker (docs/plans/theme-layers.md), in Settings → Appearance and the launcher's
// sheet. A top line says what you see and which layer each half comes from; three tabs are the layers
// (this device, me, this workspace); each has the same theme tiles and a brightness with a glass
// switch, and the first row of each half passes it through to the layers below.
import { useState, type ReactNode } from 'react'
import {
  useCurrentWorkspace,
  useResolvedLook,
  useSettings,
  useThemeProfiles,
  useWorkspaces,
} from '@/db/hooks'
import { mutate } from '@/runtime'
import { patchSettings } from '@/lib/settingsWrite'
import { useAccess } from '@/state/access'
import { useDevice } from '@/state/appearance'
import { ALL } from '@/kinds/workspace/schema'
import { personEntryLayer, withPersonLayer, type PersonLook } from '@/lib/look'
import {
  BRIGHTNESSES,
  BRIGHTNESS_LABEL,
  LAYER_TAB,
  carrierOfLayer,
  layerOfCarrier,
  lookStoreOfLayer,
  passThrough,
  patchLayer,
  seeLine,
  writableLayers,
  type Brightness,
  type LayerName,
  type LayerPatch,
  type LayerSetting,
} from '@/lib/themeLayers'
import { savedTiles, themeName, type ThemePick } from '@/lib/themePick'
import { myAuthorKey } from '@/lib/themes/actions'
import type { Hue } from '@/lib/colour'
import type { WorkspaceSpec } from '@/kinds/workspace/schema'
import { ThemeTiles } from '@/components/LookPicker'
import { Switch } from '@/components/Switch'
import { GlassSettings } from '@/components/GlassSettings'
import { cx } from '@/components/ui'

const Row = ({
  checked,
  onClick,
  label,
  hint,
  testId,
}: {
  checked: boolean
  onClick: () => void
  label: string
  hint: string
  testId: string
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    onClick={onClick}
    className="w-full flex items-center gap-3 px-3.5 min-h-12 py-2 text-left rounded-xl border border-line hover:border-line-strong mb-2"
    data-testid={testId}
  >
    <span className="min-w-0 flex-1">
      <span className="block text-[15px]">{label}</span>
      <span className="block text-xs text-faint">{hint}</span>
    </span>
    <span
      className={cx(
        'h-5 w-5 shrink-0 rounded-full border-2',
        checked
          ? 'border-accent bg-accent shadow-[inset_0_0_0_3px_var(--color-panel)]'
          : 'border-line-strong',
      )}
      aria-hidden
    />
  </button>
)

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="mb-4">
    <h3 className="px-1 pb-1.5 text-[13px] font-semibold text-muted">{title}</h3>
    {children}
  </div>
)

/** `target`: the suite slug (or `*` for Everything) whose person and workspace layers the tabs edit; default, the one on screen. */
export const ThemePicker = ({ target }: { target?: string }) => {
  const settings = useSettings()
  const suites = useWorkspaces()
  const shownSuite = useCurrentWorkspace()
  const shown = shownSuite?.spec.slug ?? ALL
  const slug = target ?? shown
  const suite = suites.find((w) => w.spec.slug === slug)
  const member = useAccess((s) => s.member)
  const admin = useAccess((s) => s.admin)
  const tabs = writableLayers({ member, admin })
  const [tab, setTab] = useState<LayerName>('device')
  const layer = tabs.includes(tab) ? tab : 'device'
  const own = settings.person_look as PersonLook | undefined
  const hasPerson = tabs.includes('person')
  const device = useDevice()
  const profiles = useThemeProfiles()
  const seen = useResolvedLook()
  // Me: for every suite, or only this one (when one is picked and she has set it apart)
  const [meOnlyPicked, setMeOnly] = useState<boolean | undefined>(undefined)
  const meOnly = !!suite && (meOnlyPicked ?? !!own?.suites?.[slug])
  const meSlug = meOnly ? slug : null

  const editing: LayerSetting =
    layer === 'device'
      ? device.layer
      : layer === 'person'
        ? personEntryLayer(own, meSlug)
        : layerOfCarrier(suite ? suite.spec : settings.look)

  const write = (patch: LayerPatch) => {
    if (layer === 'device') return device.set(patch)
    const next = patchLayer(editing, patch)
    if (layer === 'person') {
      void patchSettings({ person_look: withPersonLayer(own, meSlug, next) })
      return
    }
    if (suite) {
      const was = suite.spec as WorkspaceSpec
      const carrier = carrierOfLayer(next)
      const change: Record<string, unknown> = {}
      for (const key of ['theme', 'palette', 'colour', 'brightness', 'glass'] as const) {
        if (JSON.stringify(was[key] ?? null) !== JSON.stringify(carrier[key])) change[key] = carrier[key]
      }
      if (Object.keys(change).length) void mutate.patch(suite.id, { spec: change })
    } else {
      const store = lookStoreOfLayer(next)
      void patchSettings({ look: Object.keys(store).length ? store : null })
    }
  }

  const pick = (p: ThemePick) => write({ theme: p.theme, colour: p.colour ?? null })
  // saved themes are a person's own, so a workspace cannot carry one
  const worn = editing.theme?.kind === 'studio' ? editing.theme : undefined
  const saved = layer === 'workspace' ? [] : savedTiles(profiles, myAuthorKey(), worn)
  const studioName =
    seen.theme.kind === 'studio'
      ? profiles.find((p) => p.id === (seen.theme.kind === 'studio' ? seen.theme.profile : undefined))?.spec
          .name
      : undefined
  const pass = passThrough(layer, hasPerson)
  const hue = (editing.colour ?? (suite?.spec.colour as Hue | undefined) ?? 'violet') as Hue
  const elsewhere = layer !== 'device' && slug !== shown
  const pickedName = suite ? suite.spec.name : ALL === slug && admin ? 'All my suites' : 'Everything'

  const brightnessOf = (b: Brightness) => editing.brightness === b
  return (
    <div data-testid="theme-picker">
      <p className="text-sm px-1 mb-3" data-testid="theme-see">
        {seeLine(seen, themeName(seen.theme, studioName))}
      </p>
      <div
        className="flex gap-1 p-1 rounded-full bg-raised mb-3"
        role="tablist"
        aria-label="Which look to set"
      >
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={layer === t}
            onClick={() => setTab(t)}
            className={cx(
              'flex-1 min-h-9 rounded-full text-sm',
              layer === t ? 'bg-panel text-fg font-medium shadow-sm' : 'text-muted',
            )}
            data-testid={`layer-tab-${t}`}
          >
            {LAYER_TAB[t]}
          </button>
        ))}
      </div>
      {layer === 'person' && suite && (
        <div className="flex gap-2 mb-3" role="radiogroup" aria-label="Where my look applies">
          {[
            { only: false, label: 'All my suites' },
            { only: true, label: suite.spec.name },
          ].map((o) => (
            <button
              key={String(o.only)}
              type="button"
              role="radio"
              aria-checked={meOnly === o.only}
              onClick={() => setMeOnly(o.only)}
              className={cx(
                'rounded-full border px-3 min-h-9 text-sm',
                meOnly === o.only ? 'border-accent bg-accent-soft text-fg' : 'border-line text-muted',
              )}
              data-testid={o.only ? 'me-scope-suite' : 'me-scope-all'}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      {elsewhere && (
        <p className="text-xs text-faint px-1 mb-2" data-testid="look-elsewhere">
          {pickedName} isn’t what’s on screen, so the line above is about the suite you’re in.
        </p>
      )}
      <Section title="Theme">
        {pass && (
          <Row
            checked={editing.theme === undefined}
            onClick={() => write({ theme: null, colour: null })}
            label={pass.label}
            hint={pass.hint}
            testId="layer-pass-theme"
          />
        )}
        <ThemeTiles
          key={`${layer}:${meSlug}:${slug}`}
          value={editing.theme}
          colour={editing.colour}
          fallbackHue={hue}
          onPick={pick}
          onAccent={layer === 'device' ? undefined : (c) => write({ colour: c })}
          saved={saved}
          studio
        />
      </Section>
      <Section title="Brightness">
        {pass && (
          <Row
            checked={editing.brightness === undefined && editing.glass === undefined}
            onClick={() => write({ brightness: null, glass: null })}
            label={pass.label}
            hint={pass.hint}
            testId="layer-pass-brightness"
          />
        )}
        <div
          className="grid grid-cols-4 gap-2 mb-3"
          role="radiogroup"
          aria-label="Brightness"
          data-testid="brightness"
        >
          {BRIGHTNESSES.map((b) => (
            <button
              key={b}
              type="button"
              role="radio"
              aria-checked={brightnessOf(b)}
              disabled={seen.fixed}
              onClick={() => write({ brightness: b })}
              className={cx(
                'rounded-xl border min-h-11 text-sm disabled:opacity-40',
                brightnessOf(b) ? 'border-accent ring-1 ring-accent' : 'border-line hover:border-line-strong',
              )}
              data-testid={`appearance-${b === 'auto' ? 'auto' : b}`}
            >
              {BRIGHTNESS_LABEL[b]}
            </button>
          ))}
        </div>
        <label className="flex items-center justify-between gap-3 px-1 min-h-11">
          <span className="min-w-0">
            <span className="block text-[15px]">Glass</span>
            <span className="block text-xs text-faint">Black with frosted panels and a soft glow</span>
          </span>
          <Switch
            on={seen.glass}
            onChange={(on) => write({ glass: on })}
            label="Glass"
            disabled={seen.fixed}
            testId="appearance-glass"
          />
        </label>
        {seen.fixed && (
          <p className="text-xs text-faint px-1 mt-1" data-testid="brightness-fixed">
            A palette has one brightness. Pick Horizon or a saved theme to choose.
          </p>
        )}
      </Section>
      {layer === 'device' && <GlassSettings />}
    </div>
  )
}
